import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { captureClickId } from './utils/click-id';
import { MarketingLayout } from './components/layout/MarketingLayout';
import { PanelLayout } from './components/layout/PanelLayout';
import { GuestRoute, ProtectedRoute } from './components/layout/ProtectedRoute';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResendVerificationPage } from './pages/auth/ResendVerificationPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ForceChangePasswordPage } from './pages/auth/ForceChangePasswordPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyPage } from './pages/auth/VerifyPage';
import { ContactPage } from './pages/marketing/ContactPage';
import { ThankYouPage } from './pages/marketing/ThankYouPage';
import { DownloadPage } from './pages/marketing/DownloadPage';
import { FeaturesPage } from './pages/marketing/FeaturesPage';
import { HomePage } from './pages/marketing/HomePage';
import { PricingPage } from './pages/marketing/PricingPage';
import { PrivacyPolicyPage } from './pages/marketing/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/marketing/TermsOfServicePage';
import { DataDeletionPage } from './pages/marketing/DataDeletionPage';
import { AccountPage } from './pages/panel/AccountPage';
import { DashboardPage } from './pages/panel/DashboardPage';
import { DevicesPage } from './pages/panel/DevicesPage';
import { LicensesPage } from './pages/panel/LicensesPage';
import { SupportPage } from './pages/panel/SupportPage';
import { ReportsPage } from './pages/panel/ReportsPage';
import { StaffGuestRoute, StaffProtectedRoute } from './components/layout/StaffProtectedRoute';
import { StaffLayout } from './components/layout/StaffLayout';
import { StaffLoginPage } from './pages/staff/StaffLoginPage';
import { StaffForceChangePasswordPage } from './pages/staff/StaffForceChangePasswordPage';
import { StaffTicketsPage } from './pages/staff/StaffTicketsPage';
import { StaffPendingChargesPage } from './pages/staff/StaffPendingChargesPage';
import { StaffChatPage } from './pages/staff/StaffChatPage';
import { StaffEarningsPage } from './pages/staff/StaffEarningsPage';
import { StaffReferralsPage } from './pages/staff/StaffReferralsPage';
import { StaffHelpPage } from './pages/staff/StaffHelpPage';
import { ErpGuestRoute, ErpProtectedRoute } from './components/layout/ErpProtectedRoute';
import { ErpLayout } from './components/layout/ErpLayout';
import { ErpLoginPage } from './pages/erp/ErpLoginPage';
import { ErpDashboardPage } from './pages/erp/ErpDashboardPage';
import { ErpPaymentOperationsPage } from './pages/erp/ErpPaymentOperationsPage';
import { ErpAgentsPage } from './pages/erp/ErpAgentsPage';
import { ErpUsersPage } from './pages/erp/ErpUsersPage';
import { ErpCreateUserPage } from './pages/erp/ErpCreateUserPage';
import { ErpCreateAgentPage } from './pages/erp/ErpCreateAgentPage';
import { ErpForceChangePasswordPage } from './pages/erp/ErpForceChangePasswordPage';
import { ErpAgentDetailPage } from './pages/erp/ErpAgentDetailPage';
import { ErpPayoutsPage } from './pages/erp/ErpPayoutsPage';
import { ErpVersionsPage } from './pages/erp/ErpVersionsPage';
import { ErpDownloadsPage } from './pages/erp/ErpDownloadsPage';
import { ErpVerifactuPage } from './pages/erp/ErpVerifactuPage';

const StripeConnectDashboardPage = lazy(async () => {
  const page = await import('./pages/panel/StripeConnectDashboardPage');
  return { default: page.StripeConnectDashboardPage };
});

export function AppRoutes() {
  // Guarda el id de click de anuncio (gclid/wbraid/gbraid) una vez al cargar la
  // app, para atribuir los leads de WhatsApp y del formulario a Google Ads.
  useEffect(() => {
    captureClickId();
  }, []);

  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route index element={<HomePage />} />
        <Route path="caracteristicas" element={<FeaturesPage />} />
        <Route path="precios" element={<PricingPage />} />
        <Route path="descargar" element={<DownloadPage />} />
        <Route path="contacto" element={<ContactPage />} />
        <Route path="gracias" element={<ThankYouPage />} />
        <Route path="privacidad" element={<PrivacyPolicyPage />} />
        <Route path="condiciones" element={<TermsOfServicePage />} />
        <Route path="eliminacion-datos" element={<DataDeletionPage />} />
        <Route path="verify/:token" element={<VerifyPage />} />
        <Route path="reset-password/:token" element={<ResetPasswordPage />} />

        <Route element={<GuestRoute />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="registro" element={<RegisterPage />} />
          <Route path="recuperar-contrasena" element={<ForgotPasswordPage />} />
          <Route path="reenviar-verificacion" element={<ResendVerificationPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path="cambiar-contrasena" element={<ForceChangePasswordPage />} />
        <Route path="panel" element={<PanelLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="informes" element={<ReportsPage />} />
          <Route path="licencias" element={<LicensesPage />} />
          <Route path="licencia" element={<LicensesPage />} />
          <Route
            path="cobros/:licenseId"
            element={(
              <Suspense fallback={<p className="muted">Cargando panel de cobros…</p>}>
                <StripeConnectDashboardPage />
              </Suspense>
            )}
          />
          <Route path="soporte" element={<SupportPage />} />
          <Route path="dispositivos" element={<DevicesPage />} />
          <Route path="cuenta" element={<AccountPage />} />
        </Route>
      </Route>

      <Route element={<StaffGuestRoute />}>
        <Route path="staff/login" element={<StaffLoginPage />} />
      </Route>

      <Route element={<StaffProtectedRoute />}>
        <Route path="staff/cambiar-contrasena" element={<StaffForceChangePasswordPage />} />
        <Route path="staff" element={<StaffLayout />}>
          <Route index element={<StaffTicketsPage mode="available" />} />
          <Route path="mis-tickets" element={<StaffTicketsPage mode="mine" />} />
          <Route path="cobros-pendientes" element={<StaffPendingChargesPage />} />
          <Route path="chat/:ticketId" element={<StaffChatPage />} />
          <Route path="ganancias" element={<StaffEarningsPage />} />
          <Route path="referidos" element={<StaffReferralsPage />} />
          <Route path="manual" element={<StaffHelpPage />} />
        </Route>
      </Route>

      <Route element={<ErpGuestRoute />}>
        <Route path="erp/login" element={<ErpLoginPage />} />
      </Route>

      <Route element={<ErpProtectedRoute />}>
        <Route path="erp/cambiar-contrasena" element={<ErpForceChangePasswordPage />} />
        <Route path="erp" element={<ErpLayout />}>
            <Route index element={<ErpDashboardPage />} />
            <Route path="cobros" element={<ErpPaymentOperationsPage />} />
          <Route path="clientes" element={<ErpUsersPage />} />
          <Route path="clientes/nuevo" element={<ErpCreateUserPage />} />
          <Route path="agentes" element={<ErpAgentsPage />} />
          <Route path="agentes/nuevo" element={<ErpCreateAgentPage />} />
          <Route path="agentes/:agentId" element={<ErpAgentDetailPage />} />
          <Route path="pagos" element={<ErpPayoutsPage />} />
          <Route path="versiones" element={<ErpVersionsPage />} />
          <Route path="descargas" element={<ErpDownloadsPage />} />
          <Route path="verifactu" element={<ErpVerifactuPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
